/**
 * The auth.md agent-registration protocol, as Nexus speaks it — NEX-4203.
 *
 * auth.md (https://workos.com/auth-md/docs) is an open profile over existing
 * OAuth standards that lets an AI agent obtain a credential to act on a human's
 * behalf. Discovery is two hops — Protected Resource Metadata at
 * `/.well-known/oauth-protected-resource` names an Authorization Server, and
 * that server's metadata carries an `agent_auth` block — and registration is a
 * `POST /agent/identity` that dispatches on a `type` field.
 *
 * ## What Nexus implements, and what it deliberately does not
 *
 * The protocol defines two flows. Nexus implements ONE of them:
 *
 * - **User claimed** — the agent surfaces a six-digit code, the human signs in
 *   to Nexus and confirms it. Implemented, and it is the whole of this file.
 *   Its `service_auth` entrypoint (agent names an email up front) is SIGN-IN and
 *   is live; its `anonymous` entrypoint (agent self-registers with no identity)
 *   is SIGN-UP and ships gated OFF — see {@link AGENT_AUTH_REGISTRATION_TYPES}.
 * - **Agent verified** — the agent's provider mints an ID-JAG asserting who the
 *   user is, and the service verifies it against the provider's JWKS. NOT
 *   implemented, and therefore NOT advertised: it needs a curated trust list of
 *   agent providers, a first-link step-up ceremony and a replay cache, and the
 *   protocol's own guidance is to advertise only what a service accepts. The
 *   `events_endpoint` (RFC 8935 Security Event Tokens) is omitted for the same
 *   reason — it exists to revoke ID-JAG delegations, of which Nexus has none.
 *
 * ## Why the constants live here rather than in the backend
 *
 * Three consumers have to agree on every string below or the protocol silently
 * mis-describes itself: the discovery documents that ADVERTISE the endpoints,
 * the controllers that SERVE them, and the auth.md prose that a human or an LLM
 * READS to find them. A path typed twice is a path that can drift, and the
 * failure mode is an agent that follows the metadata to a 404 — which reads as
 * "Nexus does not support this" rather than as a bug.
 */

import { z } from "zod";

// ---------------------------------------------------------------------------
// Registration types
// ---------------------------------------------------------------------------

/**
 * The auth.md `identity_types_supported` values Nexus knows about.
 *
 * `service_auth` is SIGN-IN: the agent names an email, and the ceremony can only
 * ever bind to a human who can already sign in to Nexus. `anonymous` is SIGN-UP
 * by another name — a registration with no identity at all, claimed later — and
 * Nexus creates organizations by hand, so it ships disabled behind the
 * `agent_auth`/`registration` SystemConfig row. Both are declared here because
 * "ready but disabled" means the code path exists and the gate is closed, not
 * that the value is unknown.
 */
export const AGENT_AUTH_REGISTRATION_TYPES = ["service_auth", "anonymous"] as const;

export type AgentAuthRegistrationType = (typeof AGENT_AUTH_REGISTRATION_TYPES)[number];

/** Lifecycle of a registration row. Only `claimed` carries a credential. */
export const AGENT_AUTH_REGISTRATION_STATUSES = [
  "pending",
  "claimed",
  "expired",
  "revoked"
] as const;

export type AgentAuthRegistrationStatus = (typeof AGENT_AUTH_REGISTRATION_STATUSES)[number];

// ---------------------------------------------------------------------------
// Grant types
// ---------------------------------------------------------------------------

/** RFC 7523 — exchange a service-signed `identity_assertion` for an access token. */
export const AGENT_AUTH_JWT_BEARER_GRANT = "urn:ietf:params:oauth:grant-type:jwt-bearer";

/**
 * The auth.md profile grant — poll with a `claim_token` until the human
 * completes the ceremony. Named in the WorkOS namespace because the profile is
 * WorkOS's; a Nexus-specific URN here would be a dialect no agent recognises.
 */
export const AGENT_AUTH_CLAIM_GRANT = "urn:workos:agent-auth:grant-type:claim";

export const AGENT_AUTH_GRANT_TYPES_SUPPORTED = [
  AGENT_AUTH_JWT_BEARER_GRANT,
  AGENT_AUTH_CLAIM_GRANT
] as const;

/** The `typ` header of a Nexus-signed identity assertion, per the protocol. */
export const AGENT_AUTH_ASSERTION_TYP = "oauth-id-jag+jwt";

// ---------------------------------------------------------------------------
// Paths — one definition, read by the metadata, the controllers and auth.md
// ---------------------------------------------------------------------------

/**
 * Every agent-auth path, relative to the backend's `/api` global prefix.
 *
 * The discovery documents publish absolute URLs built from these; the
 * controllers declare routes from them. A second spelling anywhere is an agent
 * following the metadata to a 404.
 */
export const AGENT_AUTH_PATHS = {
  /** RFC 9728 Protected Resource Metadata. */
  protectedResourceMetadata: "/api/.well-known/oauth-protected-resource",
  /** RFC 8414 Authorization Server metadata, carrying the `agent_auth` block. */
  authorizationServerMetadata: "/api/.well-known/oauth-authorization-server",
  /** The prose companion to the PRM. */
  authMd: "/api/auth.md",
  /** Registration dispatch — `POST`, dispatches on `type`. */
  identity: "/api/agent/identity",
  /** Claim-ceremony start, for `anonymous` registrations only. */
  identityClaim: "/api/agent/identity/claim",
  /** RFC 6749 token endpoint — both grants above. */
  token: "/api/oauth2/token",
  /** RFC 7009 revocation. */
  revoke: "/api/oauth2/revoke"
} as const;

/**
 * The console route a human lands on to confirm a code. Not under `/api`: this
 * is the one surface of the protocol an agent never touches and a browser always
 * does. `verification_uri` points here with the claim-attempt token attached.
 */
export const AGENT_AUTH_CLAIM_PAGE_PATH = "/agent-auth/claim";

/** Query parameter carrying the claim-attempt token into the claim page. */
export const AGENT_AUTH_CLAIM_ATTEMPT_PARAM = "claim_attempt_token";

// ---------------------------------------------------------------------------
// Ceremony parameters
// ---------------------------------------------------------------------------

/**
 * How long a registration stays claimable. Ten minutes is the RFC 8628 default
 * for a device-authorization code and it is the right order of magnitude here:
 * the human is being asked to switch to a browser and type six digits NOW.
 */
export const AGENT_AUTH_CLAIM_TTL_SECONDS = 600;

/**
 * Seconds the agent must wait between `/oauth2/token` polls. Advertised as
 * `interval` and enforced — a faster poll gets `slow_down`, per RFC 8628.
 */
export const AGENT_AUTH_POLL_INTERVAL_SECONDS = 5;

/**
 * Wrong-code submissions allowed against ONE claim attempt before it is burned.
 *
 * A six-digit code is 20 bits: it is guess-bounded by lockout, not by entropy.
 * Five is enough for a human mistyping and leaves an attacker a 5-in-10^6 chance
 * per ceremony, after which they need a fresh `user_code` they cannot request.
 */
export const AGENT_AUTH_MAX_CODE_ATTEMPTS = 5;

/** Digits in the `user_code`. RFC 8628 shape; the protocol specifies six. */
export const AGENT_AUTH_USER_CODE_LENGTH = 6;

/** Lifetime of a minted access token, in seconds. Reported as `expires_in`. */
export const AGENT_AUTH_ACCESS_TOKEN_TTL_SECONDS = 60 * 60 * 24 * 30;

/**
 * Lifetime of the `identity_assertion`, in seconds.
 *
 * Longer than the access token on purpose: the assertion is what the agent
 * re-exchanges at `/oauth2/token` to refresh, so an assertion that died first
 * would force a whole new human ceremony every time a token lapsed. The protocol
 * forbids issuing a refresh token precisely because the assertion IS the refresh
 * credential.
 */
export const AGENT_AUTH_ASSERTION_TTL_SECONDS = 60 * 60 * 24 * 90;

/** Prefix of the plaintext claim token handed to the agent exactly once. */
export const AGENT_AUTH_CLAIM_TOKEN_PREFIX = "clm_";

/** Prefix of the claim-attempt token embedded in `verification_uri`. */
export const AGENT_AUTH_CLAIM_ATTEMPT_TOKEN_PREFIX = "cla_";

// ---------------------------------------------------------------------------
// Error codes
// ---------------------------------------------------------------------------

/**
 * Protocol error codes Nexus emits. The OAuth-standard ones
 * (`authorization_pending`, `slow_down`, `expired_token`, `invalid_grant`,
 * `invalid_request`, `invalid_scope`) are spelled exactly as RFC 6749/8628
 * define them, because an agent library branches on the string.
 */
export const AGENT_AUTH_ERRORS = {
  invalidRequest: "invalid_request",
  invalidGrant: "invalid_grant",
  invalidScope: "invalid_scope",
  unsupportedGrantType: "unsupported_grant_type",
  /** The ceremony has not been completed by the human yet — keep polling. */
  authorizationPending: "authorization_pending",
  /** Polled faster than the advertised `interval`. */
  slowDown: "slow_down",
  /** The claim window closed, or the registration was revoked. */
  expiredToken: "expired_token",
  /** The `type` is known to the protocol but not accepted here (see below). */
  unsupportedRegistrationType: "unsupported_registration_type",
  /** `anonymous` arrived while agent-initiated sign-up is gated off. */
  registrationDisabled: "registration_disabled",
  /** The claim token is unknown, already used, or belongs to another ceremony. */
  invalidClaimToken: "invalid_claim_token",
  /** A claim ceremony is already in flight, or the registration is claimed. */
  claimedOrInFlight: "claimed_or_in_flight",
  /** Nexus cannot sign assertions — the surface is not provisioned. */
  temporarilyUnavailable: "temporarily_unavailable"
} as const;

export type AgentAuthErrorCode = (typeof AGENT_AUTH_ERRORS)[keyof typeof AGENT_AUTH_ERRORS];

// ---------------------------------------------------------------------------
// Wire schemas — POST /agent/identity
// ---------------------------------------------------------------------------

/**
 * Scopes an agent may ask for.
 *
 * WILDCARDS ARE REFUSED, and that is the point rather than an omission.
 * `checkScope` honours `*:*`, `agents:*` and `*:read`, so a wildcard here would
 * let an agent request — and a human unwittingly approve, from one line of UI —
 * a grant far wider than the words on the screen. An agent enumerates what it
 * needs, and the claim page shows exactly that list.
 */
export const AgentAuthRequestedScopeSchema = z
  .string()
  .min(3)
  .max(64)
  .regex(
    /^[a-z][a-z0-9_]*:[a-z]+$/,
    "A scope is `resource:action`, and wildcards are not accepted"
  );

export const AgentAuthRegisterRequestSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("service_auth"),
    /**
     * RFC 6749 `login_hint`. This is an ASSERTION BY THE AGENT and is never
     * treated as proof of anything: it binds the ceremony so that only a human
     * signed in with this address can complete it. Nexus does not reveal whether
     * the address has an account — a registration for an unknown email succeeds
     * and simply can never be claimed.
     */
    login_hint: z.string().email().max(320),
    agent_label: z.string().max(120).optional(),
    scope: z.string().max(2048).optional()
  }),
  z.object({
    type: z.literal("anonymous"),
    agent_label: z.string().max(120).optional(),
    scope: z.string().max(2048).optional()
  })
]);

export type AgentAuthRegisterRequest = z.infer<typeof AgentAuthRegisterRequestSchema>;

/** The RFC 8628-shaped block the agent surfaces to the human. */
export const AgentAuthClaimBlockSchema = z.object({
  user_code: z.string(),
  expires_in: z.number().int().positive(),
  verification_uri: z.string().url(),
  interval: z.number().int().positive()
});

export const AgentAuthRegisterResponseSchema = z.object({
  registration_id: z.string(),
  registration_type: z.enum(AGENT_AUTH_REGISTRATION_TYPES),
  claim_url: z.string(),
  claim_token: z.string(),
  claim_token_expires: z.string(),
  post_claim_scopes: z.array(z.string()),
  /**
   * Present for `service_auth`, where the ceremony starts at registration.
   * Absent for `anonymous`, whose ceremony starts at `/agent/identity/claim`.
   */
  claim: AgentAuthClaimBlockSchema.optional(),
  /**
   * ALWAYS EMPTY on Nexus, and returned so the field's absence is not read as an
   * oversight. The protocol lets an `anonymous` registration act with reduced
   * scopes before it is claimed; Nexus has no principal to act as before a human
   * is known, so there is nothing to grant and the token endpoint refuses an
   * unclaimed registration outright.
   */
  pre_claim_scopes: z.array(z.string()).optional()
});

export type AgentAuthRegisterResponse = z.infer<typeof AgentAuthRegisterResponseSchema>;

// ---------------------------------------------------------------------------
// Wire schemas — POST /agent/identity/claim
// ---------------------------------------------------------------------------

export const AgentAuthStartClaimRequestSchema = z.object({
  claim_token: z.string().min(8).max(128),
  email: z.string().email().max(320)
});

export const AgentAuthStartClaimResponseSchema = z.object({
  registration_id: z.string(),
  claim_attempt_id: z.string(),
  status: z.literal("initiated"),
  expires_at: z.string(),
  claim_attempt: AgentAuthClaimBlockSchema
});

// ---------------------------------------------------------------------------
// Wire schemas — POST /oauth2/token and /oauth2/revoke
// ---------------------------------------------------------------------------

/**
 * Both grants, as `application/x-www-form-urlencoded` fields.
 *
 * Not a discriminated union on `grant_type`: an unknown grant must answer
 * `unsupported_grant_type`, and a discriminated union rejects it as a malformed
 * body instead — a different error, on a field the agent got right.
 */
export const AgentAuthTokenRequestSchema = z.object({
  grant_type: z.string().min(1).max(128),
  claim_token: z.string().max(128).optional(),
  assertion: z.string().max(8192).optional(),
  resource: z.string().max(2048).optional()
});

export const AgentAuthTokenResponseSchema = z.object({
  access_token: z.string(),
  token_type: z.literal("Bearer"),
  expires_in: z.number().int().positive(),
  scope: z.string(),
  identity_assertion: z.string(),
  assertion_expires: z.string()
});

export type AgentAuthTokenResponse = z.infer<typeof AgentAuthTokenResponseSchema>;

export const AgentAuthRevokeRequestSchema = z.object({
  token: z.string().min(1).max(8192),
  token_type_hint: z.string().max(64).optional()
});

/** The protocol's error body. `error` is the machine-readable half. */
export const AgentAuthErrorResponseSchema = z.object({
  error: z.string(),
  error_description: z.string().optional()
});
